/**
 * .tuMetrics {"tu1": 0.88, "tu2": 0.9, "tu3": 4, "notes": "Follow list + DB notify fan-out for parent objects", "tuVersion": "2.5"}
 * .help "Who is following which cards, sprints, features, projects, roadmaps, and plans — so Following in the feed and comment pings stay in sync."
 */
import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import useFeedStore from './feedStore';
import useUserStore from './userStore';
import type { FeedEvent } from '../types';
import {
  followerUserIdsMatchingRefs,
  isFollowEntityType,
  uniqueFollowRefs,
  type FollowEntityType,
  type FollowRef,
} from '../features/feed/followMatch';

export type Follow = {
  id: string;
  userId: string;
  entityType: FollowEntityType;
  entityId: string;
  createdAt: string;
};

interface FollowState {
  follows: Follow[];
  isLoading: boolean;
  error: string | null;
  initializeFollows: () => Promise<void>;
  followEntity: (entityType: FollowEntityType, entityId: string, entityTitle: string) => Promise<void>;
  unfollowEntity: (entityType: FollowEntityType, entityId: string) => Promise<void>;
  isFollowing: (entityType: FollowEntityType, entityId: string) => boolean;
  followersOf: (entityType: string, entityId: string) => Follow[];
  notifyFollowers: (
    refs: FollowRef[],
    skipUserIds: Array<string | null | undefined>,
    event: Omit<FeedEvent, 'id' | 'createdAt' | 'read' | 'userId'>,
  ) => Promise<void>;
}

function followEventIds(entityType: FollowEntityType, entityId: string) {
  return {
    ...(entityType === 'task' || entityType === 'idea' ? { cardId: entityId } : {}),
    ...(entityType === 'sprint' ? { sprintId: entityId } : {}),
    ...(entityType === 'roadmap' ? { roadmapId: entityId } : {}),
    ...(entityType === 'requirement' ? { requirementId: entityId } : {}),
    ...(entityType === 'project' ? { projectId: entityId } : {}),
    ...(entityType === 'plan' ? { planId: entityId } : {}),
  };
}

const useFollowStore = create<FollowState>((set, get) => ({
  follows: [],
  isLoading: false,
  error: null,

  initializeFollows: async () => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) {
        set({ follows: [], isLoading: false });
        return;
      }

      const { data, error } = await supabase
        .from('user_follows')
        .select('*')
        .eq('company_id', currentUser.companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const follows = (data ?? [])
        .filter((row) => isFollowEntityType(row.entity_type))
        .map((row) => ({
          id: row.id,
          userId: row.user_id,
          entityType: row.entity_type as FollowEntityType,
          entityId: String(row.entity_id).trim(),
          createdAt: row.created_at,
        }));

      set({ follows, isLoading: false });
    } catch (error) {
      console.error('Error loading follows:', error);
      set({ error: 'Failed to load follows', isLoading: false });
    }
  },

  followEntity: async (entityType, entityId, entityTitle) => {
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');
      if (!currentUser.companyId) throw new Error('Current user is not associated with a company.');
      if (!isFollowEntityType(entityType)) throw new Error(`Cannot follow ${entityType}`);

      const { data, error } = await supabase
        .from('user_follows')
        .insert({
          entity_type: entityType,
          entity_id: entityId.trim(),
          user_id: currentUser.id,
          company_id: currentUser.companyId,
        })
        .select()
        .single();

      if (error) throw error;

      const follow: Follow = {
        id: data.id,
        userId: data.user_id,
        entityType: data.entity_type as FollowEntityType,
        entityId: String(data.entity_id).trim(),
        createdAt: data.created_at,
      };

      await useFeedStore.getState().addEvent({
        type: 'entity_followed',
        title: `Following ${entityType}`,
        description: `${currentUser.name} started following "${entityTitle}"`,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        ...followEventIds(entityType, entityId.trim()),
      });

      set((state) => ({
        follows: [follow, ...state.follows],
      }));
    } catch (error) {
      console.error('Error following entity:', error);
    }
  },

  unfollowEntity: async (entityType, entityId) => {
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');
      if (!currentUser.companyId) throw new Error('Current user is not associated with a company.');

      const { error } = await supabase
        .from('user_follows')
        .delete()
        .match({
          user_id: currentUser.id,
          entity_type: entityType,
          entity_id: entityId.trim(),
          company_id: currentUser.companyId,
        });

      if (error) throw error;

      set((state) => ({
        follows: state.follows.filter(
          (f) => !(f.userId === currentUser.id && f.entityType === entityType && f.entityId === entityId.trim())
        ),
      }));
    } catch (error) {
      console.error('Error unfollowing entity:', error);
    }
  },

  isFollowing: (entityType, entityId) => {
    const me = useUserStore.getState().currentUser?.id;
    if (!me) return false;
    return get().follows.some(
      (f) => f.userId === me && f.entityType === entityType && f.entityId.trim() === entityId.trim()
    );
  },

  followersOf: (entityType, entityId) => {
    return get().follows.filter((f) =>
      f.entityId.trim() === entityId.trim()
      && (f.entityType === entityType
        || (entityType === 'card' && (f.entityType === 'task' || f.entityType === 'idea'))
        || (entityType === 'feature' && f.entityType === 'requirement'))
    );
  },

  notifyFollowers: async (refs, skipUserIds, event) => {
    const cleaned = uniqueFollowRefs(refs);
    if (cleaned.length === 0) return;

    const skip = [...skipUserIds, event.initiatorId];
    const currentUser = useUserStore.getState().currentUser;
    const ids = [...new Set(cleaned.map((r) => r.entityId))];

    let rows: Array<{ userId: string; entityType: string; entityId: string }> = get().follows.map((f) => ({
      userId: f.userId,
      entityType: f.entityType,
      entityId: f.entityId,
    }));

    if (currentUser?.companyId) {
      const { data, error } = await supabase
        .from('user_follows')
        .select('user_id, entity_type, entity_id')
        .eq('company_id', currentUser.companyId)
        .in('entity_id', ids);

      if (!error && data) {
        const fromDb = data.map((row) => ({
          userId: row.user_id as string,
          entityType: row.entity_type as string,
          entityId: String(row.entity_id).trim(),
        }));
        const seen = new Set(fromDb.map((r) => `${r.userId}:${r.entityType}:${r.entityId}`));
        rows = [
          ...fromDb,
          ...rows.filter((r) => !seen.has(`${r.userId}:${r.entityType}:${r.entityId}`)),
        ];
      }
    }

    const recipients = followerUserIdsMatchingRefs(rows, cleaned, skip);
    if (recipients.length === 0) return;

    const feed = useFeedStore.getState();
    await Promise.all(recipients.map((userId) => feed.addEvent({ ...event, userId })));
  },
}));

export default useFollowStore;
